/**
 * The climb.
 *
 * Reads a level from generateLevel(), renders the tower, and turns typed words
 * into tweened hops. Motion is tweened, never physics (CLAUDE.md). All art goes
 * through asset keys, so Prompt 10 swaps in SVGs without touching this file.
 */
import Phaser from 'phaser';
import { animalAssetKeys, AnimalSprite, DEFAULT_ANIMAL } from '../art/AnimalSprite';
import { queueArt } from '../art/loadArt';
import { showLoadingBar } from './ui/loadingBar';
import { WordList } from './game/WordList';
import { KeyboardHint } from './game/KeyboardHint';
import { nextKeyHint } from '../systems/keyboardHints';
import { isTouchDevice, setTouchTyping, touchTypingOpen } from '../systems/touchKeyboard';
import { Autoplayer } from '../systems/autoplay';
import { audioOf } from '../systems/audioRegistry';
import type { SfxName } from '../systems/soundDesign';
import { backgroundLayerKey } from '../data/themes';
import { animalById, bodyTintFor, COLOR_SWATCHES } from '../data/animals';
import { themeBandFor } from '../data/themes';
import type { AnimalId } from '../data/animals';
import type { SaveManager } from '../systems/SaveManager';
import {
  devAnimalFromQuery,
  devLevelFromQuery,
  devHintsOn,
  devWordListOn,
  devAutoplayOptions,
  devWordSeed,
  setDevState,
} from '../systems/devQuery';
import { generateLevel, MAX_LEVEL, wordRefillFor } from '../systems/LevelGenerator';
import type { WordRefill } from '../systems/LevelGenerator';
import {
  addLevelToRun,
  isCaught,
  isNearWater,
  paceForLevel,
  startTideRun,
  startWater,
  stepWater,
  summarizeRun,
  tideStarsFor,
  waterMargin,
  waterTiming,
} from '../systems/TideRun';
import type {
  TideLevelClear,
  TideLevelTyping,
  CosmeticId,
  TideOutcome,
  TideRun,
  WaterState,
} from '../systems/TideRun';
import type { TideSummaryData } from './TideSummary';
import { Water } from './game/Water';
import { SentenceBar } from './game/SentenceBar';
import { ensurePlaceholderTexture } from '../art/placeholders';
import { effectivePlatform, isBonusKind, resolveDrop, resolveLanding } from '../systems/PowerUps';
import {
  applyCorrect,
  applyMiss,
  createScoreState,
  finalScore,
  multiplierForStreak,
  starsForAccuracy,
  wpmFor,
  STREAK_FOR_2X,
} from '../systems/Scoring';
import type { ScoreState } from '../systems/Scoring';
import { charMatches, isCorrect, TypingInput } from '../systems/TypingInput';
import {
  animalY,
  arcHeightFor,
  arcPoint,
  clampPlatformIndex,
  jumpDurationFor,
  platformY,
  towerBounds,
  wordPanelY,
} from '../systems/tower';
import { Background } from './game/Background';
import { Hud } from './game/Hud';
import { PauseMenu } from './game/PauseMenu';
import { PowerUpFx } from './game/PowerUpFx';
import { TowerView } from './game/Tower';
import { WordPanel } from './game/WordPanel';
import type { PanelTone } from './game/WordPanel';
import {
  FONT_FAMILY,
  GAME_HEIGHT,
  GAME_WIDTH,
  IS_PORTRAIT,
  PALETTE,
  PORTRAIT_DOCK_TOP,
  PORTRAIT_PANEL_Y,
  stageX,
  TYPING_AREA_BOTTOM,
} from '../types';
import type { GameRunStats, LevelConfig, PlatformKind, PlatformSpec, WordSlip } from '../types';

const ANIMAL_DEPTH = 20;

/** A new word salt for each play of a level. */
function freshSalt(): number {
  return 1 + Math.floor(Math.random() * 0x7ffffffe);
}

/** The correction is non-blocking and clears on the next keystroke. */
const CORRECTION_MS = 900;

const DROP_MS = 460;
const DROP_ARC = 26;

/** Caps how far behind the typing the animal can ever fall. */
const MAX_MOVE_MS = 620;

/** Lifts the streak sparkle from the animal's feet to its middle. */
const STREAK_SPARKLE_RISE = 52;

/** How long the take-off crouch shows before the jump frame. */
const CROUCH_MS = 70;

const TAKEOFF_SQUASH_X = 1.12;
const TAKEOFF_SQUASH_Y = 0.88;
const LANDING_SQUASH_X = 1.14;
const LANDING_SQUASH_Y = 0.86;

type MoveStyle = 'jump' | 'spring' | 'jetpack' | 'drop';

const ARC_SCALE: Readonly<Record<MoveStyle, number>> = {
  jump: 1,
  spring: 1.25,
  jetpack: 0.5,
  drop: 1,
};

const DURATION_SCALE: Readonly<Record<MoveStyle, number>> = {
  jump: 1,
  spring: 1,
  jetpack: 1.1,
  drop: 1,
};

function moveStyleFor(kind: PlatformKind): MoveStyle {
  if (kind === 'spring') return 'spring';
  if (kind === 'jetpack') return 'jetpack';
  return 'jump';
}

const CONFETTI_TEXTURE = 'fx/confetti';

/** How long the goal celebration plays before Results takes over. */
const CELEBRATION_MS = 1600;

/** Camera follow lerp while climbing normally; launches tighten this up. */
const FOLLOW_LERP = 0.12;

/** Tide Run: the breather between one level's goal and the next tower. */
const TIDE_BREATHER_MS = 1900;
/** Tide Run: how long the splash plays before the summary. */
const CAUGHT_MS = 2000;
const HEARTBEAT_MS = 850;
/** Tide Run, tall stage: the animal rides this much higher, so the water below can show. */
const TIDE_PORTRAIT_LIFT = 80;

export interface GameSceneData {
  readonly level?: number;
  readonly animal?: string;
  /** Present in a Tide Run: everything the run carries from level to level. */
  readonly tide?: TideRun;
  /** Fixes the words, to replay an exact level. Left out, every play draws new ones. */
  readonly salt?: number;
}

export class Game extends Phaser.Scene {
  private config!: LevelConfig;
  private tower!: TowerView;
  private background!: Background;
  private animal!: AnimalSprite;
  private panel!: WordPanel;
  private hud!: Hud;
  private pauseMenu!: PauseMenu;
  private typing!: TypingInput;
  private fx!: PowerUpFx;
  /** Only present when the player has turned the word list on in Settings. */
  private wordList: WordList | undefined;
  /** Settings > Keyboard hints: the on-screen keyboard. */
  private keyboardHint: KeyboardHint | undefined;

  private finalStats: GameRunStats | null = null;
  private levelNumber = 1;
  private animalId = DEFAULT_ANIMAL;
  private currentIndex = 0;
  private finished = false;
  /** The current spring or jetpack word had a slip, so its boost is gone. */
  private bonusLost = false;
  /** On phones: the prompt to tap, which is what opens the phone keyboard. */
  private tapToType: Phaser.GameObjects.Text | undefined;
  /** The HUD mute, for dev levels that have no profile to save it in. */
  private mutedWithoutProfile = false;
  /** Settings > Auto enter: submit a correct word without Space or Enter. */
  private autoEnter = true;
  private moveToken = 0;
  private moveTween: Phaser.Tweens.Tween | undefined;
  /** Wall-clock moment the Results scene is due, once the goal is reached. */
  private resultsDueAt = 0;
  private celebrated = false;
  private resultsStarted = false;
  private startedAt = 0;

  private score: ScoreState = createScoreState();
  private keystrokes = 0;
  private keystrokeHits = 0;
  private springsUsed = 0;
  private jetpacksUsed = 0;
  private bonusWordsMissed = 0;
  private readonly missedWords: string[] = [];
  private readonly missedLetters: Record<string, number> = {};
  /** Words that had a slip, for the review on Results. */
  private readonly wordSlips: WordSlip[] = [];
  /** Wrong keys pressed on the word currently shown. */
  private slipsThisWord = 0;

  private blurHandler: (() => void) | undefined;
  /** Dev-only: `?autoplay=1` types the level by itself (Prompt 13 QA). */
  private autoplayer: Autoplayer | undefined;

  /** Tide Run: the run so far, or null in normal play. */
  private tide: TideRun | null = null;
  private water: WaterState = startWater();
  private waterView: Water | undefined;
  private waterSeconds: (platform: number) => number = () => 6;
  /** This play's word salt: new each time a level starts, unless a caller fixes it. */
  private salt = 0;
  /** Replacement words for platforms dropped back to after a miss. */
  private refill: WordRefill | undefined;
  /** Sentence levels: the whole sentence, with the current word picked out. */
  private sentenceBar: SentenceBar | undefined;
  /** Tide Run: time actually played on this level, pauses left out. */
  private levelMs = 0;
  private caught = false;
  private nextHeartbeatAt = 0;

  constructor() {
    super('Game');
  }

  init(data: GameSceneData): void {
    this.levelNumber = data.level ?? devLevelFromQuery() ?? 1;
    this.salt = data.salt ?? (import.meta.env.DEV ? devWordSeed() : null) ?? freshSalt();
    // The player's chosen animal, from their profile, unless a caller names
    // one. Screens like Results start levels without saying, and used to get
    // the default penguin instead of the animal the player picked.
    this.animalId =
      data.animal ??
      this.saveManager()?.activeProfile()?.selectedAnimal ??
      devAnimalFromQuery() ??
      DEFAULT_ANIMAL;
    this.tide = data.tide ?? null;
    this.resetRunState();
  }

  /**
   * Load what this level needs that the menus did not.
   *
   * Preload only fetches the menu set, so the chosen animal's full frame set
   * and this band's background layers are pulled in here. Phaser runs the
   * loader before create(), and anything missing still falls back to a
   * placeholder or to the code-drawn environment.
   */
  preload(): void {
    const level = generateLevel(this.levelNumber);
    const keys = [...animalAssetKeys(this.animalId)];
    for (const layer of ['sky', 'far', 'near'] as const) {
      keys.push(backgroundLayerKey(level.backgroundTheme, layer));
    }
    queueArt(this, keys);
    showLoadingBar(this);
  }

  create(): void {
    // Levels are generated in design space; the tall stage squeezes them into
    // its narrower column (stageX is the identity on the wide stage).
    // Fresh words every play; the layout is the level's own (LevelGenerator).
    // Tide Run is words only, so its sentence levels play as word levels.
    const generated = generateLevel(this.levelNumber, {
      salt: this.salt,
      sentences: this.tide === null,
      // The player's grade gives them their own word ramp.
      grade: this.saveManager()?.grade() ?? null,
    });
    this.refill = wordRefillFor(generated, this.salt);
    this.config = {
      ...generated,
      platforms: generated.platforms.map((platform) => ({ ...platform, x: stageX(platform.x) })),
    };
    // The bed changes with the band, so the music shifts as the climb does.
    audioOf(this)?.startMusic(this.config.backgroundTheme);
    this.cameras.main.setBackgroundColor(PALETTE.sky);
    const band = themeBandFor(this.config.level);
    this.background = new Background(
      this,
      band.background,
      this.reducedMotion(),
      band.ambient,
      // The sky pans across the whole gradient over the height of the tower.
      Math.abs(platformY(this.config.platformCount - 1) - platformY(0)),
    );

    this.tower = new TowerView(this, this.config);
    this.fx = new PowerUpFx(this);

    this.animal = new AnimalSprite(
      this,
      this.startPlatform().x,
      animalY(0),
      this.animalId,
      this.chosenBodyTint(),
    );
    this.animal.setDepth(ANIMAL_DEPTH);
    this.animal.setAnimalState('idle');

    this.panel = new WordPanel(this, this.startPlatform().x, wordPanelY(1));
    // Tall stage: the word sits in one fixed spot just above where the device
    // keyboard opens, rather than floating over the narrow tower.
    if (IS_PORTRAIT) {
      this.panel.setScrollFactor(0);
      this.drawPortraitDock();
    }

    // Scene instances are reused, so a list from a previous level must not
    // survive into this one even when the setting is now off.
    this.autoEnter = this.saveManager()?.settings().autoEnter ?? true;

    this.wordList = undefined;
    // Neither the word list nor the on-screen keyboard fits the tall stage,
    // and a phone has its own keyboard on show anyway.
    const wantsList =
      !IS_PORTRAIT &&
      (this.saveManager()?.settings().wordList === true ||
        (import.meta.env.DEV && devWordListOn()));
    if (wantsList) {
      this.wordList = new WordList(this, this.config.platforms, this.reducedMotion());
      this.wordList.refresh(this.currentIndex);
    }

    this.keyboardHint = undefined;
    const wantsHints =
      !IS_PORTRAIT &&
      (this.saveManager()?.settings().keyboardHints === true ||
        (import.meta.env.DEV && devHintsOn()));
    if (wantsHints) this.keyboardHint = new KeyboardHint(this, this.reducedMotion());

    this.hud = new Hud(
      this,
      this.config.level,
      this.config.platformCount,
      {
        // The same button pauses and, showing play, resumes.
        onPause: () => (this.pauseMenu.isOpen ? this.closePause() : this.openPause()),
        onToggleMute: () => this.toggleMute(),
      },
      this.tide !== null,
    );
    this.hud.setMuted(this.isMuted());
    this.setupTide();

    this.pauseMenu = new PauseMenu(this, {
      onResume: () => this.closePause(),
      onRestart: () => this.restartLevel(),
      onQuit: () => this.quitToMap(),
    });

    this.setupCamera();
    this.setupInput();
    this.showWordForNextPlatform();

    this.autoplayer = undefined;
    const autoplay = import.meta.env.DEV ? devAutoplayOptions() : null;
    if (autoplay !== null) {
      this.autoplayer = new Autoplayer(autoplay, {
        ready: () => this.typing.enabled && !this.finished && !this.pauseMenu.isOpen,
        word: () => this.nextPlatform()?.word ?? '',
      });
    }

    // Phones: this screen takes typing, so a tap opens the phone keyboard.
    setTouchTyping(true);
    this.tapToType = undefined;
    if (isTouchDevice()) {
      this.tapToType = this.add
        .text(
          GAME_WIDTH / 2,
          IS_PORTRAIT ? TYPING_AREA_BOTTOM + 90 : TYPING_AREA_BOTTOM - 40,
          'Tap here to type',
          {
            fontFamily: FONT_FAMILY,
            fontSize: '30px',
            color: '#1b2a41',
            backgroundColor: '#ffc145',
            padding: { x: 22, y: 12 },
          },
        )
        .setOrigin(0.5)
        .setScrollFactor(0)
        .setDepth(150);
    }

    this.startedAt = this.time.now;
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.teardown());
  }

  /* ----------------------------------------------------------------- setup */

  private setupCamera(): void {
    const bounds = towerBounds(this.config.platformCount);
    const camera = this.cameras.main;
    // Tide Run, tall stage: room below the start floe, so the camera can ride
    // high enough from the very first word for the water to show beneath.
    const extra = IS_PORTRAIT && this.tide !== null ? TIDE_PORTRAIT_LIFT : 0;
    camera.setBounds(bounds.x, bounds.y, bounds.width, bounds.height + extra);
    camera.startFollow(this.animal, true, FOLLOW_LERP, FOLLOW_LERP);
    if (IS_PORTRAIT) {
      // Tall stage: the device keyboard covers the bottom, so keep the animal
      // in the upper part with the next platforms and the word above it.
      // The animal rides just above the word dock, the platforms ahead above it.
      camera.setDeadzone(240, 80);
      const lift = this.tide === null ? 0 : TIDE_PORTRAIT_LIFT;
      camera.setFollowOffset(0, -Math.round(GAME_HEIGHT / 2 - (PORTRAIT_DOCK_TOP - 70 - lift)));
    } else {
      camera.setDeadzone(400, 180);
      // Ride low on screen, so the next few platforms are visible above.
      camera.setFollowOffset(0, 120);
    }
  }

  private setupInput(): void {
    this.typing = new TypingInput(this, {
      onCharacter: (char, index) => {
        // Unlocks the AudioContext on the player's first real key press, which
        // is the only moment a browser will allow it.
        audioOf(this)?.unlock();
        this.sound_('key');
        this.onCharacter(char, index);
      },
      onBackspace: () => this.panel.setTyped(this.typing.typed),
      onSubmit: (typed) => this.onSubmit(typed),
      // Early sentence levels don't need the full stop: auto enter sends the
      // word without it, so a stop typed straight after is dropped, not
      // counted against the next word.
      accept: (char, index) => !(char === '.' && index === 0 && this.config.match === 'lenient'),
    });
    this.typing.allowFullStop = this.config.sentenceLevel;
    this.panel.setMatch(this.config.match);
    // Tall stage: just above the word dock. Wide: along the foot of the
    // screen, or just above the keyboard hints when they are on.
    const barBottom = IS_PORTRAIT
      ? PORTRAIT_DOCK_TOP - 12
      : this.keyboardHint !== undefined
        ? this.keyboardHint.bounds.top - 12
        : GAME_HEIGHT - 24;
    this.sentenceBar = this.config.sentenceLevel ? new SentenceBar(this, barBottom) : undefined;

    this.input.keyboard?.on('keydown', (event: KeyboardEvent) => {
      if (this.pauseMenu.isOpen) {
        if (event.key === 'Escape') this.closePause();
        else this.pauseMenu.handleKey(event.key);
        return;
      }
      // No bare-letter hotkeys: every letter belongs to the word being typed,
      // so an `M` for mute would fire while typing "moon". Esc is safe.
      if (event.key === 'Escape') this.openPause();
    });

    // CLAUDE.md: pause on window blur.
    this.blurHandler = (): void => {
      if (!this.finished) this.openPause();
    };
    this.game.events.on(Phaser.Core.Events.BLUR, this.blurHandler);
  }

  /* ----------------------------------------------------------------- input */

  private onCharacter(char: string, index: number): void {
    this.keystrokes += 1;
    const next = this.nextPlatform();
    const target = next?.word ?? '';
    if (charMatches(char, target[index], this.config.match)) {
      this.keystrokeHits += 1;
    } else {
      // A slip on a spring or jetpack word loses the power-up straight away;
      // typing the word right still earns an ordinary hop.
      if (next !== undefined && isBonusKind(next.kind) && !this.bonusLost) this.loseBonus();
      const expected = target[index];
      this.slipsThisWord += 1;
      if (expected !== undefined) {
        this.missedLetters[expected] = (this.missedLetters[expected] ?? 0) + 1;
      }
    }
    this.panel.setTyped(this.typing.typed);
    this.refreshHud();
    this.publishDevState();

    // Auto enter: a word typed exactly right goes straight through. Anything
    // else still waits for Space or Enter, so a wrong word is never skipped
    // and a slip can be backspaced first.
    if (this.autoEnter && isCorrect(this.typing.typed, target, this.config.match)) {
      this.onSubmit(this.typing.typed);
    }
  }

  private refreshHud(): void {
    this.hud.setStats(this.score.wordsAttempted, this.keystrokeAccuracy());
    this.hud.setScore(this.score.points, multiplierForStreak(this.score.streak));
  }

  /**
   * Typing is never blocked. The level state advances the instant a word is
   * submitted; the animal's motion is a visual that chases the new position and
   * retargets if another word lands mid-flight. A child who types ahead is never
   * penalised, and a future speed mode is not capped by our own animations.
   */
  private onSubmit(typed: string): void {
    if (this.finished) return;
    const next = this.nextPlatform();
    const target = next?.word;
    if (next === undefined || target === undefined) return;

    const correct = isCorrect(typed, target, this.config.match);
    if (!correct || this.slipsThisWord > 0) {
      this.wordSlips.push({ word: target, typed, missed: !correct, slips: this.slipsThisWord });
    }
    this.slipsThisWord = 0;

    if (correct) {
      this.sound_('correct');
      this.advance(next, target);
      return;
    }
    this.sound_('wrong');
    this.stumble(next, target, typed);
  }

  private advance(planned: PlatformSpec, target: string): void {
    const next = effectivePlatform(planned, this.bonusLost);
    this.score = applyCorrect(this.score, target, isBonusKind(next.kind));
    const { landing, distance } = resolveLanding(
      this.currentIndex,
      next,
      this.config.platformCount,
    );

    if (next.kind === 'spring') this.springsUsed += 1;
    else if (next.kind === 'jetpack') this.jetpacksUsed += 1;

    this.wordList?.hit(next.index, landing);
    this.currentIndex = landing;
    this.wordList?.refresh(landing);
    this.typing.clear();
    this.panel.clearCorrection();
    this.hud.setProgress(landing);
    this.refreshHud();

    if (landing >= this.config.platformCount - 1) {
      // Reaching the goal is the one place typing stops, so the player cannot
      // type past the end of the level.
      this.finished = true;
      this.typing.enabled = false;
      this.publishDevState();
    } else {
      this.showWordForNextPlatform();
    }

    this.playMove(landing, distance, moveStyleFor(next.kind));
  }

  private stumble(next: PlatformSpec, target: string, typed: string): void {
    this.score = applyMiss(this.score);
    this.missedWords.push(target);

    if (isBonusKind(next.kind) && !this.bonusLost) this.loseBonus();

    this.wordList?.miss(next.index);
    const from = this.currentIndex;
    const landing = resolveDrop(from);
    this.currentIndex = landing;
    // Dropped back below a word already typed right: that platform gets a new
    // word, so a miss is answered with fresh practice, not a free retype.
    if (landing < from) this.giveFreshWord(from);
    this.wordList?.refresh(landing);
    this.typing.clear();
    this.hud.setProgress(landing);
    this.refreshHud();

    // The flash corrects the word that was actually missed, then hands over
    // to the next word: the fresh one on the platform just dropped below.
    this.showWordForNextPlatform();
    this.panel.flashCorrection(typed, CORRECTION_MS, target);

    this.playMove(landing, 1, 'drop');
  }

  /* ---------------------------------------------------------------- motion */

  /**
   * One cancellable visual move. Any new move kills the one in flight and starts
   * from wherever the animal currently is, so chained launches read as one
   * continuous flight rather than a queue.
   */
  private playMove(landing: number, distance: number, style: MoveStyle): void {
    const token = (this.moveToken += 1);
    this.stopMove();

    const from = { x: this.animal.x, y: this.animal.y };
    const to = { x: this.platformAt(landing).x, y: animalY(landing) };
    const drop = style === 'drop';

    const arc = drop ? DROP_ARC : arcHeightFor(distance) * ARC_SCALE[style];
    const duration = drop
      ? DROP_MS
      : Math.min(MAX_MOVE_MS, jumpDurationFor(distance) * DURATION_SCALE[style]);

    this.startStyleEffects(style, token);
    if (!drop) this.sound_(style);

    // Crouch, then jump. The crouch is purely visual and the travel tween
    // starts immediately underneath it, so the anticipation never costs the
    // player a frame of input (CLAUDE.md: typing is never blocked).
    this.animal.setAnimalState(drop ? 'oops' : 'crouch');
    if (!drop) {
      this.time.delayedCall(CROUCH_MS, () => {
        if (this.moveToken === token && this.animal.active) {
          this.animal.setAnimalState('jump');
        }
      });
    }
    this.animal.setSquash(drop ? 1 : TAKEOFF_SQUASH_X, drop ? 1 : TAKEOFF_SQUASH_Y);

    // Unsquash over the first part of the flight rather than in a blocking tween.
    this.tweens.add({
      targets: this.animal,
      scaleX: this.animal.baseScale,
      scaleY: this.animal.baseScale,
      duration: Math.min(160, duration * 0.4),
      ease: 'Quad.easeOut',
    });

    const progress = { t: 0 };
    this.moveTween = this.tweens.add({
      targets: progress,
      t: 1,
      duration,
      ease: drop ? 'Quad.easeIn' : style === 'jetpack' ? 'Sine.easeInOut' : 'Quad.easeOut',
      onUpdate: () => {
        const point = arcPoint(from, to, progress.t, arc);
        this.animal.setPosition(point.x, point.y);
        if (style === 'jetpack') this.fx.positionJetpack(point.x, point.y);
        if (style === 'spring') this.fx.positionTrail(point.x, point.y);
        if (!drop && progress.t > 0.6 && this.animal.animalState !== 'fall') {
          this.animal.setAnimalState('fall');
        }
      },
      onComplete: () => {
        if (token !== this.moveToken) return;
        this.moveTween = undefined;
        this.animal.setPosition(to.x, to.y);
        this.finishMove(style, distance);
      },
    });
  }

  private startStyleEffects(style: MoveStyle, token: number): void {
    this.fx.stopTrail();
    if (this.reducedMotion()) return;

    if (style === 'spring') {
      void this.fx.showSpring(this.animal.x, this.animal.y);
      this.fx.startTrail(this.animal.x, this.animal.y);
      // Released on its own timeline: waiting for it would reintroduce a lock.
      this.time.delayedCall(180, () => {
        if (token === this.moveToken) this.fx.releaseSpring();
      });
    } else if (style === 'jetpack') {
      this.fx.attachJetpack(this.animal.x, this.animal.y);
    }
  }

  private finishMove(style: MoveStyle, distance: number): void {
    this.fx.stopTrail();
    if (style === 'jetpack') this.fx.detachJetpack();
    if (style === 'spring' || style === 'jetpack') {
      this.popBonusText(distance, style === 'spring' ? PALETTE.teal : PALETTE.sunset);
    }

    this.animal.setAnimalState(style === 'drop' ? 'idle' : 'land');
    this.landingSquash();
    this.landingNudge(style);
    if (style !== 'drop') this.sound_('land');

    if (this.finished && this.currentIndex >= this.config.platformCount - 1) {
      this.celebrate();
      return;
    }
    this.publishDevState();
  }

  /** A quick squash on arrival, purely decorative and never blocking. */
  private landingSquash(): void {
    this.tweens.add({
      targets: this.animal,
      scaleX: this.animal.baseScale * LANDING_SQUASH_X,
      scaleY: this.animal.baseScale * LANDING_SQUASH_Y,
      duration: 70,
      yoyo: true,
      onComplete: () => {
        this.animal.resetSquash();
        if (!this.finished) this.animal.setAnimalState('idle');
      },
    });
  }

  private stopMove(): void {
    this.moveTween?.remove();
    this.moveTween = undefined;
  }

  /** "+2!" or "+4!" over the landing spot (CLAUDE.md). */
  private popBonusText(distance: number, color: string): void {
    if (distance <= 1) return;
    this.fx.floatText(this.animal.x, this.animal.y - 110, `+${distance}!`, color);
  }

  /* ------------------------------------------------------------------ goal */

  /**
   * Reaching the goal. Deliberately defensive: this is the end of a child's
   * level and must never be the place the game gets stuck.
   */
  private celebrate(): void {
    if (this.celebrated) return;
    this.celebrated = true;
    this.finished = true;
    this.typing.enabled = false;

    // Clear any paused state before scheduling. A pause menu left open, or a
    // scene clock stopped by a blur race, must not strand the player here.
    if (this.pauseMenu.isOpen) this.pauseMenu.hide();
    this.hud.setPaused(false);
    this.tweens.resumeAll();
    this.time.paused = false;

    this.animal.setAnimalState('cheer');
    this.panel.setVisible(false);
    this.sentenceBar?.show(null);
    this.publishDevState();

    if (!this.reducedMotion()) {
      this.confetti();
      this.cameras.main.shake(180, 0.004);
    }

    const stats = this.buildStats();
    if (this.tide !== null) {
      this.clearTideLevel();
      return;
    }
    const save = this.saveManager();
    if (save !== null) {
      const result = save.recordRun(stats);
      // Results reads both of these from the registry.
      this.registry.set(`best.level${stats.level}.score`, result.previousBestScore);
      this.registry.set('newUnlock', result.newUnlocks[0] ?? '');
    }
    this.finalStats = stats;

    // Driven from update() rather than a delayedCall: if the loop is frozen
    // (tab hidden) nothing is skipped, and the moment it resumes the handover
    // happens. A stalled scene clock can no longer strand the celebration.
    this.resultsDueAt = performance.now() + CELEBRATION_MS;
  }

  override update(_time: number, delta: number): void {
    this.background.update();
    this.syncStreakSparkle();
    this.syncWordListFade();
    this.syncKeyboardHint();
    this.syncSentenceFade();
    this.tapToType?.setVisible(!touchTypingOpen());
    this.autoplayer?.tick(performance.now());
    if (this.tide !== null) this.updateTide(delta);
    if (this.caught) {
      if (!this.resultsStarted && performance.now() >= this.resultsDueAt) {
        this.resultsStarted = true;
        this.endTideRun('caught');
      }
      return;
    }
    if (!this.finished) return;
    // The goal was reached but the landing tween never completed (interrupted,
    // or cancelled by a retarget). Celebrate anyway rather than strand the player.
    if (!this.celebrated) {
      this.celebrate();
      return;
    }
    if (this.resultsStarted) return;
    if (performance.now() < this.resultsDueAt) return;
    this.startResults();
  }

  /**
   * Sparkle follows the animal while the streak is worth a 2x multiplier.
   *
   * Tied to STREAK_FOR_2X rather than its own number, so the effect marks the
   * thing the player is actually being rewarded for. Off under reduced motion
   * (CLAUDE.md), and stopped the moment a streak breaks.
   */
  private syncStreakSparkle(): void {
    const wanted = !this.finished && !this.reducedMotion() && this.score.streak >= STREAK_FOR_2X;
    if (!wanted) {
      this.fx.stopStreakSparkle();
      return;
    }
    // The animal's origin is its feet, so lift the emitter to its middle.
    const y = this.animal.y - STREAK_SPARKLE_RISE;
    this.fx.startStreakSparkle(this.animal.x, y);
    this.fx.positionStreakSparkle(this.animal.x, y);
  }

  /** Fade the word list back while the animal or the word panel is over it. */
  /**
   * Tall stage: a solid dock from just above the word to the bottom edge, so
   * the word sits in one calm place and the tower below the animal is hidden
   * (the device keyboard covers most of it anyway), plus a soft strip behind
   * the HUD so platforms never run under the score.
   */
  private drawPortraitDock(): void {
    const top = PORTRAIT_DOCK_TOP;
    const dock = this.add.graphics().setScrollFactor(0).setDepth(28);
    dock.fillStyle(0x0b1220, 0.25);
    dock.fillRect(0, top - 16, GAME_WIDTH, 16);
    dock.fillStyle(0x16233a, 1);
    dock.fillRoundedRect(0, top, GAME_WIDTH, GAME_HEIGHT - top + 40, 36);
    dock.lineStyle(3, 0x8ecae6, 0.6);
    dock.lineBetween(36, top, GAME_WIDTH - 36, top);

    const strip = this.add.graphics().setScrollFactor(0).setDepth(27);
    // Near-solid behind the HUD text (to ~y 140), then a short fade.
    strip.fillStyle(0x0b1220, 0.72);
    strip.fillRect(0, 0, GAME_WIDTH, 144);
    for (let i = 0; i < 24; i += 1) {
      strip.fillStyle(0x0b1220, 0.72 * (1 - (i + 1) / 25));
      strip.fillRect(0, 144 + i * 3, GAME_WIDTH, 3);
    }
  }

  /** Light the next key, and fade the keyboard back when the animal is behind it. */
  private syncKeyboardHint(): void {
    const hint = this.keyboardHint;
    if (hint === undefined) return;
    const target = this.nextPlatform()?.word ?? '';
    const live = !this.finished && !this.pauseMenu.isOpen && this.typing.enabled;
    hint.show(
      live ? nextKeyHint(this.typing.typed, target, this.autoEnter, this.config.match) : null,
    );

    const camera = this.cameras.main;
    const x = this.animal.x - camera.scrollX;
    const feet = this.animal.y - camera.scrollY;
    const body = new Phaser.Geom.Rectangle(x - 45, feet - 95, 90, 95);
    hint.setFaded(Phaser.Geom.Intersects.RectangleToRectangle(body, hint.bounds));
  }

  /** The sentence bar fades while the animal is behind it, as the keyboard hints do. */
  private syncSentenceFade(): void {
    const bar = this.sentenceBar;
    if (bar === undefined) return;
    const camera = this.cameras.main;
    const x = this.animal.x - camera.scrollX;
    const feet = this.animal.y - camera.scrollY;
    const body = new Phaser.Geom.Rectangle(x - 45, feet - 95, 90, 95);
    bar.setFaded(Phaser.Geom.Intersects.RectangleToRectangle(body, bar.bounds));
  }

  private syncWordListFade(): void {
    const list = this.wordList;
    if (list === undefined) return;
    const clear = list.right + 14;
    const animalOver = this.animal.x - 46 < clear;
    const panelOver = this.panel.visible && this.panel.x - this.panel.halfWidth < clear;
    list.setFaded(animalOver || panelOver);
  }

  private startResults(): void {
    if (this.resultsStarted) return;
    this.resultsStarted = true;
    if (this.tide !== null) {
      this.nextTideLevel(this.tide);
      return;
    }
    this.scene.start('Results', this.finalStats ?? this.buildStats());
  }

  /* ------------------------------------------------------------ tide run */

  /** Platform `index` gets a new word of the same difficulty, never one already in the level. */
  private giveFreshWord(index: number): void {
    const platform = this.config.platforms[index];
    if (platform?.word === undefined || this.refill === undefined) return;
    const fresh = this.refill.next(platform.kind);
    // A spring or jetpack platform gets a new boost distance with its word;
    // a normal one never had one. On a sentence level the new word is a
    // stepping stone outside the sentence, which picks up again after it.
    const updated: PlatformSpec = {
      index: platform.index,
      x: platform.x,
      kind: platform.kind,
      word: fresh.word,
      wordScore: fresh.wordScore,
      ...(fresh.bonusDistance === undefined ? {} : { bonusDistance: fresh.bonusDistance }),
      ...(platform.theme === undefined ? {} : { theme: platform.theme }),
    };
    this.config = {
      ...this.config,
      platforms: this.config.platforms.map((p, i) => (i === index ? updated : p)),
    };
    this.wordList?.replaceWord(index, fresh.word);
    // Tide Run's water crosses each platform at its word's typing time.
    if (this.tide !== null) this.timeWater();
  }

  private timeWater(): void {
    const run = this.tide;
    if (run === null) return;
    this.waterSeconds = waterTiming(
      this.config.platforms.map((platform) => platform.word),
      paceForLevel(run.baseWpm, run.clears.length),
    );
  }

  private setupTide(): void {
    const audio = audioOf(this);
    if (this.tide === null) {
      audio?.setTempo(1);
      return;
    }
    this.timeWater();
    this.water = startWater();
    this.waterView = new Water(this, this.config.backgroundTheme, this.reducedMotion());
    this.levelMs = 0;
    this.caught = false;
    this.nextHeartbeatAt = 0;
    this.hud.setTideClock(this.tide.elapsedMs, this.tide.clears.length);
  }

  /**
   * The water rises only while the level is live: never during a pause, the
   * breather after a goal, or the splash. Steps are capped so a frame that
   * arrives late (a busy device) can't jump the water past the player.
   */
  private updateTide(delta: number): void {
    const run = this.tide;
    const view = this.waterView;
    if (run === null || view === undefined) return;
    const now = performance.now();
    const live = !this.finished && !this.caught && !this.pauseMenu.isOpen;
    if (live) {
      const step = Math.min(100, Math.max(0, delta));
      this.levelMs += step;
      this.water = stepWater(this.water, step / 1000, this.waterSeconds);
    }
    view.update(this.water.position, now);

    const margin = waterMargin(this.water, this.currentIndex);
    const danger = live && isNearWater(this.water, this.currentIndex);
    view.setDanger(danger);
    this.hud.setWater(this.water.position, margin, danger);
    this.hud.setTideClock(run.elapsedMs + this.levelMs, run.clears.length);

    // Faster music as the run goes on, and faster again with the water close.
    const tempo = 1 + Math.min(0.3, run.clears.length * 0.03);
    audioOf(this)?.setTempo(danger ? tempo * 1.3 : tempo);
    if (danger && now >= this.nextHeartbeatAt) {
      this.sound_('heartbeat');
      this.nextHeartbeatAt = now + HEARTBEAT_MS;
    }

    if (live && isCaught(this.water, this.currentIndex)) this.catchAnimal();
  }

  /** This level's typing, as the run counts it. */
  private levelTyping(): TideLevelTyping {
    return {
      elapsedMs: this.levelMs,
      correctChars: this.score.correctChars,
      wordsCorrect: this.score.wordsCorrect,
      wordsAttempted: this.score.wordsAttempted,
      keystrokes: this.keystrokes,
      keystrokeHits: this.keystrokeHits,
      missedWords: [...this.missedWords],
      missedLetters: { ...this.missedLetters },
    };
  }

  /** The goal, in a Tide Run: save the clear, show it, then the next tower. */
  private clearTideLevel(): void {
    const run = this.tide;
    if (run === null) return;
    const timeSec = Math.round(this.levelMs / 100) / 10;
    const stars = tideStarsFor(
      timeSec,
      this.config.platforms.map((platform) => platform.word),
    );
    // Rewards are granted here, the moment they are earned, not at the end
    // of the run: a run left through the pause menu never reaches the end.
    const result = this.saveManager()?.recordTideClear(
      this.config.level,
      timeSec,
      stars,
      run.clears.length + 1,
    ) ?? {
      clear: { level: this.config.level, timeSec, stars, newBest: true } satisfies TideLevelClear,
      newCosmetics: [],
      newUnlocks: [],
    };
    const next = addLevelToRun(run, this.levelTyping(), result.clear);
    this.tide = {
      ...next,
      newCosmetics: [...next.newCosmetics, ...result.newCosmetics],
      newAnimals: [...next.newAnimals, ...result.newUnlocks],
    };
    this.waterView?.setDanger(false);
    if (result.newCosmetics.length > 0) this.sound_('unlock');
    this.showClearBanner(result.clear, result.newCosmetics);
    this.publishDevState();
    this.resultsDueAt = performance.now() + TIDE_BREATHER_MS;
  }

  private nextTideLevel(run: TideRun): void {
    if (this.config.level >= MAX_LEVEL) {
      this.endTideRun('summit');
      return;
    }
    this.scene.restart({ level: this.config.level + 1, animal: this.animalId, tide: run });
  }

  /** "Level 4 cleared, 38.2 s" with its tide stars, and any colour just earned. */
  private showClearBanner(clear: TideLevelClear, earned: readonly CosmeticId[]): void {
    ensurePlaceholderTexture(this, 'ui/star_full');
    ensurePlaceholderTexture(this, 'ui/star_empty');
    const y = IS_PORTRAIT ? Math.round(PORTRAIT_DOCK_TOP * 0.55) : 250;
    const width = Math.min(560, GAME_WIDTH - 40);
    const box = this.add.graphics();
    box.fillStyle(0x0b1220, 0.86);
    box.fillRoundedRect(-width / 2, -78, width, 156, 22);
    box.lineStyle(3, 0x8ecae6, 0.9);
    box.strokeRoundedRect(-width / 2, -78, width, 156, 22);
    const title = this.add
      .text(0, -46, `Level ${clear.level} cleared!`, {
        fontFamily: FONT_FAMILY,
        fontSize: '38px',
        color: PALETTE.snow,
      })
      .setOrigin(0.5);
    const time = this.add
      .text(0, 4, `${clear.timeSec.toFixed(1)} s${clear.newBest ? '   New best!' : ''}`, {
        fontFamily: FONT_FAMILY,
        fontSize: '26px',
        color: clear.newBest ? PALETTE.gold : PALETTE.ice,
      })
      .setOrigin(0.5);
    const stars = [0, 1, 2].map((i) =>
      this.add
        .image((i - 1) * 46, 48, i < clear.stars ? 'ui/star_full' : 'ui/star_empty')
        .setDisplaySize(36, 36),
    );
    const parts: Phaser.GameObjects.GameObject[] = [box, title, time, ...stars];
    const names = earned
      .map((id) => COLOR_SWATCHES.find((swatch) => swatch.cosmetic === id)?.name)
      .filter((name): name is string => name !== undefined);
    if (names.length > 0) {
      box.fillStyle(0xffc145, 1);
      box.fillRoundedRect(-width / 2 + 24, 84, width - 48, 48, 16);
      parts.push(
        this.add
          .text(0, 108, `New colour: ${names.join(' and ')}!`, {
            fontFamily: FONT_FAMILY,
            fontSize: '26px',
            color: PALETTE.ink,
          })
          .setOrigin(0.5),
      );
    }
    const banner = this.add
      .container(GAME_WIDTH / 2, y, parts)
      .setScrollFactor(0)
      .setDepth(140);
    if (!this.reducedMotion()) {
      banner.setScale(0.7).setAlpha(0);
      this.tweens.add({ targets: banner, scale: 1, alpha: 1, duration: 260, ease: 'Back.easeOut' });
    }
  }

  /** The water reaches the animal: a splash, a bob, then the summary. */
  private catchAnimal(): void {
    const run = this.tide;
    if (run === null || this.caught) return;
    this.caught = true;
    this.finished = true;
    this.typing.enabled = false;
    this.stopMove();
    this.fx.stopTrail();
    this.panel.setVisible(false);
    this.sentenceBar?.show(null);
    this.waterView?.setDanger(false);
    audioOf(this)?.setTempo(1);
    this.sound_('splash');
    this.tide = addLevelToRun(run, this.levelTyping(), null);

    const surface = Water.surfaceY(this.water.position);
    this.animal.setAnimalState('oops');
    this.animal.setPosition(this.animal.x, surface + 26);
    if (!this.reducedMotion()) {
      this.splash(this.animal.x, surface);
      this.tweens.add({
        targets: this.animal,
        y: surface + 18,
        duration: 520,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
    }
    this.publishDevState();
    this.resultsDueAt = performance.now() + CAUGHT_MS;
  }

  private splash(x: number, y: number): void {
    if (!this.textures.exists(CONFETTI_TEXTURE)) this.confettiTexture();
    const emitter = this.add.particles(x, y, CONFETTI_TEXTURE, {
      speed: { min: 140, max: 320 },
      angle: { min: 220, max: 320 },
      gravityY: 700,
      lifespan: { min: 500, max: 900 },
      scale: { min: 0.4, max: 0.9 },
      quantity: 18,
      tint: [0xffffff, 0xbfe9ff, 0x8ecae6],
      emitting: false,
    });
    emitter.setDepth(ANIMAL_DEPTH + 6);
    emitter.explode(26);
    this.time.delayedCall(1200, () => emitter.destroy());
  }

  /** The run is over: fold it into the save and show the summary. */
  private endTideRun(outcome: TideOutcome): void {
    const run = this.tide;
    if (run === null) return;
    const summary = summarizeRun(run, outcome, this.config.level);
    const result = this.saveManager()?.recordTideRun(summary, run) ?? {
      newRecords: [],
      newCosmetics: [],
      newUnlocks: [],
      tideStarsTotal: 0,
    };
    // The summary announces everything the run earned, mid-run grants included.
    const data: TideSummaryData = {
      summary,
      result: {
        ...result,
        newCosmetics: [...run.newCosmetics, ...result.newCosmetics],
        newUnlocks: [...run.newAnimals, ...result.newUnlocks],
      },
    };
    this.scene.start('TideSummary', data);
  }

  /**
   * Leaving a run through the pause menu still counts it: its records, its
   * typing in the history, and anything earned. There is just no summary.
   */
  private recordStoppedRun(): void {
    const run = this.tide;
    if (run === null || this.caught || this.celebrated) return;
    const stopped = addLevelToRun(run, this.levelTyping(), null);
    if (stopped.elapsedMs <= 0) return;
    this.saveManager()?.recordTideRun(summarizeRun(stopped, 'stopped', this.config.level), stopped);
  }

  private confettiTexture(): void {
    const g = this.make.graphics({ x: 0, y: 0 }, false);
    g.fillStyle(0xffffff, 1);
    g.fillRect(0, 0, 10, 14);
    g.generateTexture(CONFETTI_TEXTURE, 10, 14);
    g.destroy();
  }

  private confetti(): void {
    if (!this.textures.exists(CONFETTI_TEXTURE)) this.confettiTexture();

    const goal = this.tower.imageAt(this.config.platformCount - 1);
    const burstX = goal?.x ?? this.animal.x;
    const burstY = (goal?.y ?? this.animal.y) - 60;
    const emitter = this.add.particles(burstX, burstY, CONFETTI_TEXTURE, {
      speed: { min: 180, max: 420 },
      angle: { min: 200, max: 340 },
      gravityY: 520,
      lifespan: { min: 900, max: 1600 },
      scale: { min: 0.6, max: 1.2 },
      rotate: { min: 0, max: 360 },
      quantity: 6,
      tint: [0xffc145, 0x2ec4b6, 0xff6b6b, 0x8ecae6, 0xf7fbff],
    });
    emitter.setDepth(ANIMAL_DEPTH + 1);
    this.time.delayedCall(900, () => emitter.stop());
    this.time.delayedCall(2600, () => emitter.destroy());
  }

  /* ----------------------------------------------------------------- pause */

  private openPause(): void {
    if (this.pauseMenu.isOpen || this.finished) return;
    this.pauseMenu.show();
    this.typing.enabled = false;
    this.tweens.pauseAll();
    this.time.paused = true;
    this.hud.setPaused(true);
    // Put a phone's keyboard away while the pause menu is up.
    setTouchTyping(false);
    this.publishDevState();
  }

  private closePause(): void {
    if (!this.pauseMenu.isOpen) return;
    this.pauseMenu.hide();
    this.tweens.resumeAll();
    this.time.paused = false;
    if (!this.finished) this.typing.enabled = true;
    this.hud.setPaused(false);
    setTouchTyping(true);
    this.publishDevState();
  }

  private restartLevel(): void {
    this.tweens.resumeAll();
    this.time.paused = false;
    // In a Tide Run, starting again means the whole run, from its first level.
    const run = this.tide;
    if (run !== null) {
      this.recordStoppedRun();
      const history = this.saveManager()?.activeProfile()?.history ?? [];
      this.scene.restart({
        level: run.startLevel,
        animal: this.animalId,
        tide: startTideRun(run.startLevel, history),
      });
      return;
    }
    this.scene.restart({ level: this.levelNumber, animal: this.animalId });
  }

  private quitToMap(): void {
    this.recordStoppedRun();
    this.tweens.resumeAll();
    this.time.paused = false;
    this.scene.start('LevelMap');
  }

  /**
   * The HUD speaker mutes the game's own audio - music and sound effects -
   * and saves it, so the next level stays quiet and Settings shows the same.
   * (It used to toggle Phaser's sound manager, which plays nothing here, so
   * the button changed and the game kept making noise.)
   */
  private toggleMute(): void {
    const muted = !this.isMuted();
    const save = this.saveManager();
    if (save !== null) {
      save.setSetting('musicMuted', muted);
      save.setSetting('sfxMuted', muted);
    } else {
      this.mutedWithoutProfile = muted;
    }
    audioOf(this)?.applySettings({ musicMuted: muted, sfxMuted: muted });
    this.sound.mute = muted;
    this.hud.setMuted(muted);
  }

  /** Muted means both music and sound effects are off. */
  private isMuted(): boolean {
    const settings = this.saveManager()?.settings();
    if (settings === undefined) return this.mutedWithoutProfile;
    return settings.musicMuted && settings.sfxMuted;
  }

  /* ----------------------------------------------------------------- words */

  /** The power-up on the current word fizzles: icon puffs away, boost gone. */
  private loseBonus(): void {
    this.bonusLost = true;
    this.bonusWordsMissed += 1;
    const spot = this.panel.fizzleIcon();
    if (spot !== null && !this.reducedMotion()) this.fx.puffAt(spot.x, spot.y, spot.tint);
  }

  private showWordForNextPlatform(): void {
    const next = this.nextPlatform();
    if (next === undefined || next.word === undefined) return;
    // Each word shown gets a fresh chance at its power-up.
    this.bonusLost = false;
    if (IS_PORTRAIT) this.panel.placeAt(GAME_WIDTH / 2, PORTRAIT_PANEL_Y);
    else this.panel.placeAt(next.x, wordPanelY(next.index));
    this.panel.setWord(next.word, this.toneFor(next));
    this.sentenceBar?.show(next.sentence ?? null);
    this.panel.setTyped('');
    this.publishDevState();
  }

  /** Dev-only telemetry for the Playwright harness. */
  private publishDevState(): void {
    setDevState({
      scene: 'Game',
      platform: this.currentIndex,
      platformCount: this.config.platformCount,
      busy: this.moveTween !== undefined,
      finished: this.finished,
      paused: this.pauseMenu.isOpen,
      word: this.nextPlatform()?.word ?? '',
      typed: this.typing.typed,
      duplicateKeys: this.typing.suppressedDuplicates,
      level: this.config.level,
      tideCleared: this.tide?.clears.length ?? -1,
      caught: this.caught,
    });
  }

  /** Spring panels glow teal, jetpacks orange (CLAUDE.md). */
  private toneFor(platform: PlatformSpec): PanelTone {
    if (platform.kind === 'spring') return 'spring';
    if (platform.kind === 'jetpack') return 'jetpack';
    return 'normal';
  }

  /* ----------------------------------------------------------------- state */

  private resetRunState(): void {
    this.currentIndex = 0;
    this.finished = false;
    this.moveToken = 0;
    // A tween from a previous play of this scene instance is already dead.
    this.moveTween = undefined;
    this.finalStats = null;
    this.resultsDueAt = 0;
    this.celebrated = false;
    this.resultsStarted = false;
    this.caught = false;
    this.levelMs = 0;
    this.waterView = undefined;
    this.score = createScoreState();
    this.keystrokes = 0;
    this.keystrokeHits = 0;
    this.springsUsed = 0;
    this.jetpacksUsed = 0;
    this.bonusWordsMissed = 0;
    this.missedWords.length = 0;
    for (const key of Object.keys(this.missedLetters)) delete this.missedLetters[key];
    this.wordSlips.length = 0;
    this.slipsThisWord = 0;
  }

  private keystrokeAccuracy(): number {
    if (this.keystrokes === 0) return 100;
    return (this.keystrokeHits / this.keystrokes) * 100;
  }

  private wordAccuracy(): number {
    if (this.score.wordsAttempted === 0) return 100;
    return (this.score.wordsCorrect / this.score.wordsAttempted) * 100;
  }

  private buildStats(): GameRunStats {
    const elapsedMs = this.time.now - this.startedAt;
    return {
      level: this.config.level,
      platformCount: this.config.platformCount,
      reachedGoal: this.finished,
      wordsAttempted: this.score.wordsAttempted,
      wordsCorrect: this.score.wordsCorrect,
      keystrokes: this.keystrokes,
      keystrokeHits: this.keystrokeHits,
      accuracy: this.keystrokeAccuracy(),
      wordAccuracy: this.wordAccuracy(),
      longestStreak: this.score.longestStreak,
      elapsedMs: elapsedMs,
      score: finalScore(this.score, this.finished),
      stars: starsForAccuracy(this.keystrokeAccuracy(), this.finished),
      wpm: wpmFor(this.score.correctChars, elapsedMs),
      bonusWordsHit: this.score.bonusWordsHit,
      springsUsed: this.springsUsed,
      jetpacksUsed: this.jetpacksUsed,
      bonusWordsMissed: this.bonusWordsMissed,
      missedWords: [...this.missedWords],
      missedLetters: { ...this.missedLetters },
      wordSlips: [...this.wordSlips],
    };
  }

  /** The swatch the player picked for this animal, or null for natural. */
  private chosenBodyTint(): number | null {
    const animal = animalById(this.animalId);
    if (animal === undefined) return null;
    const profile = this.saveManager()?.activeProfile() ?? null;
    return bodyTintFor(animal, profile?.colorPerAnimal[this.animalId as AnimalId]);
  }

  private saveManager(): SaveManager | null {
    const save: unknown = this.registry.get('save');
    return save === undefined || save === null ? null : (save as SaveManager);
  }

  /** Settings writes this flag; default is full motion. */
  /**
   * A camera kick on landing, sized to what just happened.
   *
   * A correct word is the common case - it fires on nearly every jump - so its
   * nudge is barely there; at the drop's strength it reads as the screen
   * shaking every few seconds. A drop is rare and is meant to land with some
   * weight, so it keeps the bigger one. Off entirely under reduced motion.
   */
  private landingNudge(style: MoveStyle): void {
    if (this.reducedMotion()) return;
    const drop = style === 'drop';
    // A correct landing is a whisper: half a pixel or so, over 50ms. It fires
    // on nearly every word, so anything stronger reads as constant shaking.
    this.cameras.main.shake(drop ? 110 : 50, drop ? 0.0035 : 0.0004, true);
  }

  /** Audio is optional: a blocked AudioContext leaves this null and silent. */
  private sound_(name: SfxName): void {
    audioOf(this)?.play(name);
  }

  private reducedMotion(): boolean {
    return this.registry.get('reducedMotion') === true;
  }

  private platformAt(index: number): PlatformSpec {
    const platform = this.config.platforms[clampPlatformIndex(index, this.config.platformCount)];
    if (platform === undefined) throw new Error(`No platform at index ${index}`);
    return platform;
  }

  private startPlatform(): PlatformSpec {
    return this.platformAt(0);
  }

  private nextPlatform(): PlatformSpec | undefined {
    const index = this.currentIndex + 1;
    if (index >= this.config.platformCount) return undefined;
    return this.config.platforms[index];
  }

  private teardown(): void {
    setTouchTyping(false);
    this.sentenceBar?.destroy();
    this.sentenceBar = undefined;
    this.waterView?.destroy();
    this.waterView = undefined;
    audioOf(this)?.setTempo(1);
    this.wordList?.destroy();
    this.wordList = undefined;
    this.keyboardHint?.destroy();
    this.keyboardHint = undefined;
    this.typing?.destroy();
    this.fx?.destroy();
    this.background?.destroy();
    if (this.blurHandler !== undefined) {
      this.game.events.off(Phaser.Core.Events.BLUR, this.blurHandler);
      this.blurHandler = undefined;
    }
  }
}

/** Re-exported so Prompt 8's background work has the stage height to hand. */
export const STAGE_HEIGHT = GAME_HEIGHT;
export { platformY };
